import { createRoute, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { useTranslation } from "@/hooks/useSafeTranslation";
import { Check } from "lucide-react";
import { rootRoute } from "./__root";
import { cn, toDisplayString, toSafeReactNode } from "@/lib/utils";

function PricingPage() {
  const { t } = useTranslation("common");
  const [interval, setInterval] = useState<"month" | "year">("month");

  const plans = useMemo(() => {
    const planIds = ["free", "starter", "pro", "team"] as const;
    const nameMap = { free: "Free", starter: "Starter", pro: "Pro", team: "Team" } as const;
    return planIds.map((id) => {
      const cap = nameMap[id];
      const priceMonth = toDisplayString(t(`pricingPage.price${cap}Month` as "pricingPage.priceFreeMonth"));
      const priceYear = toDisplayString(t(`pricingPage.price${cap}Year` as "pricingPage.priceFreeYear"));
      const featuresKey = `pricingPage.features${cap}` as "pricingPage.featuresFree";
      const featuresResolved = t(featuresKey, { returnObjects: true });
      const featuresRaw = Array.isArray(featuresResolved) ? featuresResolved : [];
      const features = featuresRaw.map((feature) => toDisplayString(feature));
      return {
        id,
        name: toDisplayString(t(`billing:${id}`)),
        price: { month: priceMonth, year: priceYear },
        features,
        popular: id === "pro",
      };
    });
  }, [t]);

  return (
    <div className="min-h-screen bg-bg">
      <div className="mx-auto max-w-5xl px-6 py-12 space-y-12">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-text-muted hover:text-accent transition-colors">
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
          {toSafeReactNode(t("pricingPage.back"))}
        </Link>

        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <img src="/icon.svg" alt="" width={48} height={48} />
          </div>
          <h1 className="text-4xl font-bold tracking-tight text-text">
            {toSafeReactNode(t("pricingPage.title"))}
          </h1>
          <p className="mx-auto max-w-xl text-text-muted leading-relaxed">
            {toSafeReactNode(t("pricingPage.subtitle"))}
          </p>
        </div>

        <div className="flex justify-center">
          <div className="inline-flex items-center gap-1 rounded-full border border-border bg-surface p-1">
            <button
              type="button"
              onClick={() => setInterval("month")}
              className={cn(
                "rounded-full px-5 py-2 text-sm font-medium transition-all",
                interval === "month"
                  ? "bg-accent text-white shadow-sm"
                  : "text-text-muted hover:text-text"
              )}
            >
              {toSafeReactNode(t("pricingPage.monthly"))}
            </button>
            <button
              type="button"
              onClick={() => setInterval("year")}
              className={cn(
                "rounded-full px-5 py-2 text-sm font-medium transition-all",
                interval === "year"
                  ? "bg-accent text-white shadow-sm"
                  : "text-text-muted hover:text-text"
              )}
            >
              {toSafeReactNode(t("pricingPage.yearly"))}
              <span className="ml-1.5 rounded-full bg-green-500/20 px-2 py-0.5 text-[10px] font-semibold text-green-400">
                {toSafeReactNode(t("pricingPage.discountBadge"))}
              </span>
            </button>
          </div>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={cn(
                "relative flex flex-col rounded-xl border bg-surface p-6 transition-shadow hover:shadow-md",
                plan.popular
                  ? "border-accent ring-2 ring-accent/50 shadow-glow-green"
                  : "border-border"
              )}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-accent px-4 py-1 text-xs font-semibold text-white shadow-sm">
                  {toSafeReactNode(t("pricingPage.popular"))}
                </div>
              )}
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-text">{toSafeReactNode(plan.name)}</h3>
                <p className="mt-1 text-2xl font-bold text-text tracking-tight">
                  {toSafeReactNode(plan.price[interval])}
                </p>
              </div>
              <ul className="flex-1 space-y-2.5 mb-6">
                {plan.features.map((f, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-text-muted">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden />
                    {toSafeReactNode(f)}
                  </li>
                ))}
              </ul>
              <Link
                to="/login"
                className={cn(
                  "block rounded-lg px-4 py-2.5 text-center text-sm font-semibold transition-colors",
                  plan.popular
                    ? "bg-accent text-white hover:bg-accent/90 shadow-sm"
                    : "border border-border bg-surface-2 text-text hover:bg-surface-3"
                )}
              >
                {plan.id === "free" ? toSafeReactNode(t("pricingPage.startFree")) : toSafeReactNode(t("pricingPage.upgradeNow"))}
              </Link>
            </div>
          ))}
        </div>

        <div className="text-center space-y-3">
          <p className="text-sm text-text-muted">
            {toSafeReactNode(t("pricingPage.plansNote"))}
          </p>
          <div className="flex justify-center gap-4">
            <a
              href="https://chromewebstore.google.com/detail/takelead"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-accent hover:underline font-medium"
            >
              {toSafeReactNode(t("pricingPage.installExtension"))}
            </a>
            <Link to="/login" className="text-sm text-accent hover:underline font-medium">
              {toSafeReactNode(t("pricingPage.signIn"))}
            </Link>
          </div>
        </div>

        <footer className="border-t border-border pt-6 text-center text-xs text-text-muted space-x-3">
          <Link to="/privacy" className="hover:text-text transition-colors">{toSafeReactNode(t("footer.privacy"))}</Link>
          <span className="text-border">·</span>
          <Link to="/terms" className="hover:text-text transition-colors">{toSafeReactNode(t("footer.terms"))}</Link>
          <span className="text-border">·</span>
          <Link to="/lawful-use" className="hover:text-text transition-colors">{toSafeReactNode(t("footer.lawfulUse"))}</Link>
        </footer>
      </div>
    </div>
  );
}

export const pricingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pricing",
  component: PricingPage,
});
